import { apiClient } from '@/lib/api'
import { saveToken, removeToken, getToken, decodeToken, isTokenExpired } from '@/lib/auth/token'
import {
  type LoginRequest,
  type AuthResponse,
  type TokenClaims,
  type RefreshTokenResponse,
} from '@/models/domain/auth'
import { type User } from '@/models/domain/user'

function extractRealmRoles(claims: TokenClaims): string[] {
  return (claims.realm_access as { roles?: string[] } | undefined)?.roles ?? []
}

function extractKeycloakClaims(claims: TokenClaims): string[] {
  const keycloakClaims: string[] = []
  const resourceAccess =
    (claims.resource_access as Record<string, { roles?: string[] }> | undefined) ?? {}
  for (const clientData of Object.values(resourceAccess)) {
    const roles = clientData.roles ?? []
    keycloakClaims.push(...roles)
  }
  return keycloakClaims
}

function resolveBooleanFlag(realmRoles: string[], roleName: string, claimValue: unknown): boolean {
  return realmRoles.includes(roleName) || claimValue === true
}

function resolveUserFlags(claims: TokenClaims): {
  locked: boolean
  sys_admin: boolean
  sys_op: boolean
  systems_all: boolean
  dbas_all: boolean
  districts_all: boolean
  locations_all: boolean
  sections_all: boolean
} {
  const realmRoles = extractRealmRoles(claims)
  return {
    locked: resolveBooleanFlag(realmRoles, 'cs_locked', claims.locked),
    sys_admin: resolveBooleanFlag(realmRoles, 'cs_sys_admin', claims.sys_admin),
    sys_op: resolveBooleanFlag(realmRoles, 'cs_sys_op', claims.sys_op),
    systems_all: resolveBooleanFlag(realmRoles, 'cs_systems_all', claims.systems_all),
    dbas_all: resolveBooleanFlag(realmRoles, 'cs_dbas_all', claims.dbas_all),
    districts_all: resolveBooleanFlag(realmRoles, 'cs_districts_all', claims.districts_all),
    locations_all: resolveBooleanFlag(realmRoles, 'cs_locations_all', claims.locations_all),
    sections_all: resolveBooleanFlag(realmRoles, 'cs_sections_all', claims.sections_all),
  }
}

function buildUserFromKeycloakClaims(claims: TokenClaims): User {
  const flags = resolveUserFlags(claims)

  return {
    email: claims.email || '',
    first_name: claims.given_name || '',
    last_name: claims.family_name || '',
    active: true,
    ...flags,
    systems: claims.systems ?? null,
    dbas: claims.dbas ?? null,
    districts: claims.districts ?? null,
    locations: claims.locations ?? null,
    sections: claims.sections ?? null,
    keycloak_claims: extractKeycloakClaims(claims),
  }
}

class AuthService {
  async login(email: string, password: string): Promise<AuthResponse> {
    const request: LoginRequest = { email, password }
    const response = await apiClient.post<AuthResponse>('/core/v5/auth/login', request)

    const claims = decodeToken(response.token)
    if (claims?.exp) {
      const expiresIn = claims.exp - Math.floor(Date.now() / 1000)
      saveToken(response.token, expiresIn)
      return response
    }

    saveToken(response.token, 240 * 60 * 60)
    return response
  }

  logout(): void {
    removeToken()
  }

  async getCurrentUser(): Promise<User | null> {
    const token = getToken()

    if (!token || isTokenExpired()) {
      return null
    }

    const claims = decodeToken(token)
    if (!claims) {
      removeToken()
      return null
    }

    if (claims.data) {
      return claims.data
    }

    return buildUserFromKeycloakClaims(claims)
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/core/v5/auth/refresh', {
      refreshToken,
    })

    saveToken(response.token, response.expiresIn)

    return response
  }

  validateToken(): boolean {
    const token = getToken()
    if (!token) return false

    if (isTokenExpired()) {
      removeToken()
      return false
    }

    const claims = decodeToken(token)
    if (!claims) {
      removeToken()
      return false
    }

    return true
  }

  getTokenClaims() {
    const token = getToken()
    if (!token) return null
    return decodeToken(token)
  }
}

export const authService = new AuthService()
